import { createSlice } from "@reduxjs/toolkit";

import {
  createWorkingUnitThunk,
  deleteWorkingUnitThunk,
  getWorkingUnitThunk,
  moveWorkingUnitThunk,
  updateWorkingUnitThunk,
} from "state/action/working_unit";

// Initial state
const initialState = {
  // Get Working Unit inisial state
  getWorkingUnit: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Create Working Unit inisial state
  createWorkingUnit: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Update Working Unit inisial state
  updateWorkingUnit: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Delete Working Unit inisial state
  deleteWorkingUnit: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  createWorkUnitInputBody: {
    company: "", // Company ID
    name: "",
    code: "",
    type: "", // directorate, division, department
    parentUnit: null, // Parent ID or null
    level: 1, // Auto-calculated based on parent
    path: "", // Auto-generated e.g., ",ROOT,PARENT,"
    description: "",
    isActive: true,
    metadata: {
      // managerId: null, // User ID
      address: "",
      phone: "",
      email: "",
    },
  },

  // Working Unit object value
  working_unit_object_value: "",
};

export const workingUnitSlice = createSlice({
  name: "workingUnit",
  initialState,
  reducers: {
    // Root fields
    setWorkUnitField: (state, action) => {
      const { field, value } = action.payload;
      state.createWorkUnitInputBody[field] = value;
    },
    // Nested Metadata fields
    setMetadataField: (state, action) => {
      const { field, value } = action.payload;
      state.createWorkUnitInputBody.metadata[field] = value;
    },
    // Handle Parent Selection (Auto-update level & type suggestion)
    setParentUnit: (state, action) => {
      const parent = action.payload; // Assumed object { id, type, level, path }
      state.createWorkUnitInputBody.parentUnit = parent ? parent.id : null;

      if (parent) {
        state.createWorkUnitInputBody.level = parent.level + 1;
        // Mock path logic
        state.createWorkUnitInputBody.path = `${parent.path || ","}${parent.id},`;

        // Auto-suggest type based on parent level
        if (parent.type === "directorate")
          state.createWorkUnitInputBody.type = "division";
        if (parent.type === "division")
          state.createWorkUnitInputBody.type = "department";
      } else {
        state.createWorkUnitInputBody.level = 1;
        state.createWorkUnitInputBody.path = ",";
        state.createWorkUnitInputBody.type = "directorate"; // Default top level
      }
    },
    resetWorkUnitForm: (state) => {
      state.createWorkUnitInputBody = initialState.createWorkUnitInputBody;
    },
    // 1. Root Level Fields
    setField: (state, action) => {
      const { field, value } = action.payload;
      state.createWorkingUnitInputBody[field] = value;
    },
    // 2. First Level Nested (settings, limits, contact-direct)
    setNestedField: (state, action) => {
      const { parent, field, value } = action.payload;
      state.createWorkingUnitInputBody[parent][field] = value;
    },
    // 3. Deep Nested (contact.address)
    setAddressField: (state, action) => {
      const { field, value } = action.payload;
      state.createWorkingUnitInputBody.contact.address[field] = value;
    },
    resetForm: (state) => {
      state.createWorkingUnitInputBody =
        initialState.createWorkingUnitInputBody;
    },
    // Update field level 1 (name, code, logo, isActive)
    setWorkingUnitField: (state, action) => {
      const { field, value } = action.payload;
      state.createWorkingUnitInputBody[field] = value;
    },

    resetWorkingUnitForm: (state) => {
      state.createWorkingUnitInputBody =
        initialState.createWorkingUnitInputBody;
    },
    setGetWorkingUnitObjectValue: (state, action) => {
      return {
        ...state,
        working_unit_object_value: action.payload,
      };
    },
    // note: clear state
    setClearGetWorkingUnitObjectValue: (state, _) => {
      return {
        ...state,
        working_unit_object_value: "",
      };
    },
    setClearGetWorkingUnit: (state, _) => {
      return {
        ...state,
        getWorkingUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearCreateWorkingUnit: (state, _) => {
      return {
        ...state,
        createWorkingUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearUpdateWorkingUnit: (state, _) => {
      return {
        ...state,
        updateWorkingUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearDeleteWorkingUnit: (state, _) => {
      return {
        ...state,
        deleteWorkingUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearAllWorkingUnitState: (state, _) => {
      return {
        ...state,
        // Get Working Unit inisial state
        getWorkingUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Create Working Unit inisial state
        createWorkingUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Update Working Unit inisial state
        updateWorkingUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Delete Working Unit inisial state
        deleteWorkingUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Working Unit object value
        working_unit_object_value: "",
      };
    },
  },
  extraReducers(builder) {
    builder
      // Get Working Unit reducer
      .addCase(getWorkingUnitThunk.pending, (state, _) => {
        return {
          ...state,
          getWorkingUnit: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getWorkingUnitThunk.fulfilled, (state, action) => {
        return {
          ...state,
          getWorkingUnit: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getWorkingUnitThunk.rejected, (state, action) => {
        return {
          ...state,
          getWorkingUnit: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // Create Working Unit reducer
      .addCase(createWorkingUnitThunk.pending, (state, _) => {
        return {
          ...state,
          createWorkingUnit: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createWorkingUnitThunk.fulfilled, (state, action) => {
        return {
          ...state,
          createWorkingUnit: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createWorkingUnitThunk.rejected, (state, action) => {
        return {
          ...state,
          createWorkingUnit: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // update Working Unit reducer
      .addCase(updateWorkingUnitThunk.pending, (state, _) => {
        return {
          ...state,
          updateWorkingUnit: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateWorkingUnitThunk.fulfilled, (state, action) => {
        return {
          ...state,
          updateWorkingUnit: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateWorkingUnitThunk.rejected, (state, action) => {
        return {
          ...state,
          updateWorkingUnit: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      });
  },
});

export const workingUnitAction = {
  ...workingUnitSlice.actions,
  getWorkingUnitThunk,
  createWorkingUnitThunk,
  updateWorkingUnitThunk,
  deleteWorkingUnitThunk,
  moveWorkingUnitThunk,
};

export function selectWorkingUnitObjectValue(state) {
  return state?.working_unit?.working_unit_object_value;
}

export function selectWorkingUnit(state) {
  return state?.working_unit;
}

export default workingUnitSlice.reducer;
